import { ApiNotificationListResponse, getNotificationsQueryKey, KaiProApiClient } from '@kaiizen/kai-pro-api';
import { TypedCacheEntry, useCachedQuery, useQueryCache, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';
import { useKaiProApiClient } from '../useKaiProApiClient';

const fetchNotifications = (kaiProApiClient: KaiProApiClient, limit?: number) => async () => kaiProApiClient.getNotifications({ limit });

export const useNotifications = (options?: { limit?: number }) => {
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache<ApiNotificationListResponse>();

  const query = useCachedQuery(
    getNotificationsQueryKey(),
    fetchNotifications(kaiProApiClient, options?.limit),
    { enabled: !!kaiProApiClient.token },
  );

  const loadMore = useQueryCacheMutation(
    async (cursor: string) => kaiProApiClient.getNotifications({ limit: options?.limit, cursor }),
    {
      onSuccess: async result => {
        const existing = await queryCache.cache.getEntry(getNotificationsQueryKey()) as TypedCacheEntry<ApiNotificationListResponse> | undefined;
        if (!existing) {
          queryCache.cache.set(getNotificationsQueryKey(), result);
          return;
        }
        queryCache.cache.set(getNotificationsQueryKey(), {
          ...result,
          notifications: [...existing.value.notifications, ...result.notifications],
        });
      },
    },
  ).mutate;

  const markReadMutation = useQueryCacheMutation(
    async (ids: string[]) => kaiProApiClient.markNotificationsRead({ ids }),
  );

  const markRead = async (ids: string[]) => {
    await markReadMutation.mutate(ids);
    const existing = await queryCache.cache.getEntry(getNotificationsQueryKey()) as TypedCacheEntry<ApiNotificationListResponse> | undefined;
    if (!existing) return;
    const now = new Date();
    const unreadIds = new Set(
      existing.value.notifications.filter(notification => !notification.readAt).map(notification => notification.id),
    );
    const newlyReadCount = ids.filter(id => unreadIds.has(id)).length;
    queryCache.cache.set(getNotificationsQueryKey(), {
      ...existing.value,
      notifications: existing.value.notifications.map(notification =>
        ids.includes(notification.id) ? { ...notification, readAt: notification.readAt ?? now } : notification
      ),
      unreadCount: Math.max(0, existing.value.unreadCount - newlyReadCount),
    });
  };

  const markAllRead = useQueryCacheMutation(
    async () => kaiProApiClient.markNotificationsRead({ markAll: true }),
    {
      onSuccess: async () => {
        const existing = await queryCache.cache.getEntry(getNotificationsQueryKey()) as TypedCacheEntry<ApiNotificationListResponse> | undefined;
        if (!existing) return;
        const now = new Date();
        queryCache.cache.set(getNotificationsQueryKey(), {
          ...existing.value,
          notifications: existing.value.notifications.map(notification => ({
            ...notification,
            readAt: notification.readAt ?? now,
          })),
          unreadCount: 0,
        });
      },
    },
  ).mutate;

  const notifications = query.data?.notifications ?? [];
  const unreadCount = query.data?.unreadCount ?? 0;
  const nextCursor = query.data?.nextCursor;

  return {
    ...query,
    notifications,
    unreadCount,
    nextCursor,
    hasMore: !!nextCursor,
    loadMore: nextCursor ? () => loadMore(nextCursor) : undefined,
    markRead,
    markAllRead,
  };
};
